import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import {
  rowAccountKeys,
  snapshotAccounts,
  venueAccountKey,
  type OpenerDescalceAccountMeta,
  type OpenerDescalceRow
} from '../../api/useOpenerDescalce';
import { hasUnreadLeg } from './descalceLegs';

/** Client-side filter state applied over the already-fetched snapshot. */
export interface DescalceFilterState {
  search: string;
  minDescalce: number;
  onlyGroupsOn: boolean;
  hideResolving: boolean;
  /**
   * Venue-account keys (`platform:accountId`) the operator has selected. The
   * vocabulary is whatever the snapshot carries — never a fixed list, since
   * the fleet is data now. EMPTY means no account scoping at all (every row),
   * so a snapshot whose accounts the FE has never heard of still renders.
   */
  accounts: string[];
  onlyDivergence: boolean;
  /** Only rows carrying an unread triage card. OFF by default. */
  onlyWithTriage: boolean;
}

/**
 * The triage marks live in the triage index, not on the snapshot row, so the
 * lookup is injected. Absent, `onlyWithTriage` hides every row rather than
 * quietly passing them all: a filter that cannot be evaluated must not present
 * an unfiltered list as a filtered one.
 */
export interface DescalceTriageLookup {
  hasTriage?: (row: OpenerDescalceRow) => boolean;
}

/** The selectable account keys, in the snapshot's own order. */
export function accountFilterOptions(
  accounts: OpenerDescalceAccountMeta[] | undefined
): { key: string; account: OpenerDescalceAccountMeta }[] {
  return snapshotAccounts({ accounts: accounts as OpenerDescalceAccountMeta[] }).map(
    account => ({
      key: venueAccountKey(account.platform, account.accountId),
      account
    })
  );
}

/**
 * Drop selected keys the snapshot no longer offers. The filter bar only
 * renders a toggle for a key the snapshot carries, so a selection that
 * outlives its account becomes un-clickable while still hiding every row —
 * the operator sees "0 rows" with no visible cause.
 */
export function pruneAccountFilter(
  selected: string[],
  accounts: OpenerDescalceAccountMeta[] | undefined
): string[] {
  const offered = new Set(accountFilterOptions(accounts).map(o => o.key));
  return selected.filter(key => offered.has(key));
}

/** Toggle one account key in/out of the selection. */
export function toggleAccountFilter(
  selected: string[],
  key: string
): string[] {
  return selected.includes(key)
    ? selected.filter(k => k !== key)
    : [...selected, key];
}

/**
 * `hideResolving` is OFF by default. `resolving` marks that ONE side is cash
 * now, never that the imbalance is zero — the backend reconstructs a settled
 * Kalshi leg from its settlement net, so a pair that settled flat already
 * arrives at `descalce: 0` and falls under the numeric floor on its own. What
 * a resolving row still shows is a real residual, and hiding that by default
 * is the failure this screen exists to prevent. The toggle stays for an
 * operator who wants a strictly-live view.
 */
export const DEFAULT_DESCALCE_FILTERS: DescalceFilterState = {
  search: '',
  minDescalce: 0.5,
  onlyGroupsOn: false,
  hideResolving: false,
  accounts: [],
  onlyDivergence: false,
  onlyWithTriage: false
};

/**
 * A row is kept when EITHER of its two venue-accounts is selected. A row spans
 * one Kalshi and one Polymarket account, so requiring both would make a
 * single-account selection match nothing — and the operator scoping to one
 * account wants every row that touches it.
 */
function matchesAccounts(row: OpenerDescalceRow, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return rowAccountKeys(row).some(key => selected.includes(key));
}

function matchesSearch(row: OpenerDescalceRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = `${row.pairName ?? ''} ${row.group.name ?? ''}`.toLowerCase();
  return haystack.includes(q);
}

/**
 * Two kinds of row are never hidden behind the numeric floor, because for
 * neither is the number a real cross-venue descalce to threshold:
 *  - `venue === null` (unconfigured / starting / error account) — exactly the
 *    "unknown" the screen exists to surface.
 *  - `descalce === null` (a leg went unread) — there is no number to compare
 *    against a floor, and dropping the row would silently retire the very
 *    rules whose exposure is unknown.
 *  - `marketsIncomplete` — the backend emits these with counts it deliberately
 *    refused to attribute. A rule whose two Polymarket tokens collide has both
 *    Poly sides forced to `0` while real size is held, so it arrives at
 *    `descalce: 0` BY CONSTRUCTION and the floor would delete the one row the
 *    backend went out of its way to keep visible.
 */
function matchesMinDescalce(row: OpenerDescalceRow, min: number): boolean {
  if (min <= 0) return true;
  if (!row.venue) return true;
  if (row.venue.descalce == null || hasUnreadLeg(row)) return true;
  if (row.venue.marketsIncomplete === true) return true;
  return row.venue.descalce >= min;
}

/**
 * A divergence is the venue read MINUS the engine belief. Where there is no
 * venue read — an unreadable account, or a leg the sweep never reached — the
 * difference is UNKNOWN, not zero, and a filter for "belief disagrees with
 * reality" must not retire exactly the rows on which the disagreement cannot
 * be measured. Those are kept.
 *
 * A `null` divergence on a fully-read row is a different claim: the engine has
 * simply published no belief for that rule (engine off, or restarted). Nothing
 * disagrees with anything there, so the filter drops it as before.
 */
function matchesDivergence(row: OpenerDescalceRow): boolean {
  if (!row.venue || hasUnreadLeg(row)) return true;
  if (row.divergence == null) return false;
  return Math.abs(row.divergence.total) > IMBALANCE_TOLERANCE;
}

export function applyDescalceFilters(
  rows: OpenerDescalceRow[],
  filters: DescalceFilterState,
  triage?: DescalceTriageLookup
): OpenerDescalceRow[] {
  return rows.filter(row => {
    if (!matchesSearch(row, filters.search)) return false;
    if (!matchesMinDescalce(row, filters.minDescalce)) return false;
    if (filters.onlyGroupsOn && row.group.podsDesired !== 'on') return false;
    if (filters.hideResolving && row.venue?.resolving === true) return false;
    if (!matchesAccounts(row, filters.accounts)) return false;
    if (filters.onlyDivergence && !matchesDivergence(row)) return false;
    if (filters.onlyWithTriage && triage?.hasTriage?.(row) !== true) return false;
    return true;
  });
}

/**
 * How many rows `hideResolving` ALONE is suppressing — rows that clear every
 * other active filter and are hidden only because a leg already resolved.
 * `0` when the filter is off. The header strip reports it so a default-on
 * filter never hides rows silently.
 */
export function countResolvingHidden(
  rows: OpenerDescalceRow[],
  filters: DescalceFilterState,
  triage?: DescalceTriageLookup
): number {
  if (!filters.hideResolving) return 0;
  return applyDescalceFilters(
    rows,
    { ...filters, hideResolving: false },
    triage
  ).filter(
    row => row.venue?.resolving === true
  ).length;
}

export interface DescalceTotals {
  rowCount: number;
  /** Sum of `venue.descalce` over NON-ambiguous, fully-read rows only. */
  totalDescalce: number;
  ambiguousCount: number;
  resolvingCount: number;
  /**
   * Rows left OUT of `totalDescalce` because their position could not be read
   * — a leg the sweep never reached, or a whole venue account that could not
   * be read at all (`venue === null`, i.e. an `error` / `unconfigured` /
   * `starting` account). Both are the same shortfall to the operator.
   */
  unreadCount: number;
  /** Visible rows carrying an unread counter-stuck / orphaned-fill card. */
  triageCount: number;
}

/**
 * Header-strip totals. Two kinds of row are excluded from the summed total:
 *  - ambiguous ones — each claimant carries the FULL market size (never an
 *    invented split, per the sweeper's own invariant), so summing them in
 *    would double (or N-fold) count contracts on a single venue position;
 *  - rows with an unread leg, and rows whose venue accounts could not be read
 *    at all — there is no number to add, and adding `0` would report an
 *    unknown as hedged. They are counted separately instead, so the total is
 *    never quietly short.
 */
export function computeDescalceTotals(
  rows: OpenerDescalceRow[],
  triage?: DescalceTriageLookup
): DescalceTotals {
  let totalDescalce = 0;
  let ambiguousCount = 0;
  let resolvingCount = 0;
  let unreadCount = 0;
  let triageCount = 0;
  for (const row of rows) {
    // Counted BEFORE the unread guard below: a row whose account is unreadable
    // can still be the one holding an open triage card.
    if (triage?.hasTriage?.(row) === true) triageCount++;
    // A missing `venue` block is the strongest form of "not read": the row's
    // accounts themselves are unreadable. It belongs in `unreadCount` with the
    // half-read rows, so the shortfall in the total always has a row count
    // beside it. Strictly stronger than main's bare `!row.venue` guard, which
    // it subsumes.
    if (!row.venue || row.venue.descalce == null || hasUnreadLeg(row)) {
      unreadCount++;
      continue;
    }
    if (row.venue.ambiguous) {
      ambiguousCount++;
      continue;
    }
    totalDescalce += row.venue.descalce;
    if (row.venue.resolving) resolvingCount++;
  }
  return {
    rowCount: rows.length,
    totalDescalce,
    ambiguousCount,
    resolvingCount,
    unreadCount,
    triageCount
  };
}
